/**
 * Substrate × calculation grid cell — wraps the 3 flat regions per scope
 * §4.1 cluster principle (flat-regions mode) + Atlas Day 2 P0 drift-
 * pattern correction.
 *
 * Single grid cell at (substrate, calculation) coordinate containing 3
 * vertically-stacked region cards (Business defaults + Path mechanics +
 * Weight components). NOT 3 separate cells — preserves locked 3×3 grid
 * geometry per scope §3.
 *
 * Substrate fetched on mount via fetchSubstrateCalcSubstrate. Each region
 * receives its substrate slice as props.
 */

import { useEffect, useState, type JSX } from 'react';
import {
  fetchSubstrateCalcSubstrate,
  type SubstrateCalcSubstrate,
} from '../../../lib/atlas/substrateCalcSubstrate';
import BusinessDefaultsRegion from './BusinessDefaultsRegion';
import PathMechanicsRegion from './PathMechanicsRegion';
import WeightComponentsRegion from './WeightComponentsRegion';

export default function SubstrateCalcCell(): JSX.Element {
  const [substrate, setSubstrate] = useState<SubstrateCalcSubstrate | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    fetchSubstrateCalcSubstrate()
      .then((s) => {
        if (!cancelled) setSubstrate(s);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div data-cell-coordinate="substrate-calculation" className="space-y-2">
      {error ? (
        <p className="text-xs text-ink-muted italic">
          Substrate × calculation substrate currently unavailable.
        </p>
      ) : !substrate ? (
        <>
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
        </>
      ) : (
        <>
          <BusinessDefaultsRegion substrate={substrate.businessDefaults} />
          <PathMechanicsRegion substrate={substrate.pathMechanics} />
          <WeightComponentsRegion substrate={substrate.weightComponents} />
        </>
      )}
    </div>
  );
}
